import {
  Component,
  Input,
  ChangeDetectionStrategy,
  Output,
  EventEmitter,
} from '@angular/core';
import { HtmlPipe } from '@app/shared/pipes/html.pipe';
import { HtmlTranslatedPipe } from '@app/shared/pipes/html-translated.pipe';
import { SearchTrackerService } from '@app/search/services/search-tracker.service';
import { isKey, Key } from '@app/shared/key';
import { SafeHtml } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'dgx-search-typo-suggestion',
  templateUrl: './search-typo-suggestion.component.html',
  styleUrls: ['./search-typo-suggestion.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SearchTypoSuggestionComponent {
  @Input() public searchTerm: string;
  @Input() public suggestedTerm: string;
  @Input() public previousSearchTerm: string;
  @Input() public typoSuggestionAutoSearched: boolean;

  @Output() public suggestionSelected = new EventEmitter<string>();

  constructor(
    private searchTrackerService: SearchTrackerService,
    private htmlTranslatedPipe: HtmlTranslatedPipe,
    private htmlPipe: HtmlPipe,
    private translateService: TranslateService
  ) {}

  private get searchedInsteadFor(): string {
    const suggestedTermHtml = `<span class="italic font-semibold">${this.searchTerm}</span>`;
    return this.translateService.instant('LearningSearch_SearchedInsteadFor', {
      suggestedTermHtml,
    });
  }

  private get didYouMean(): SafeHtml {
    const suggestedTermHtml = `<a class="italic color-blue font-semibold" role="link" tabindex="0">${this.suggestedTerm}</a>`;
    return this.translateService.instant('LearningSearch_DidYouMean', {
      suggestedTermHtml,
    });
  }

  private get noResultsFound(): SafeHtml {
    // This HTML contains a user entered value so we sanitize it
    const termHtml = this.htmlPipe
      .transform`<span class="italic font-semibold">${this.previousSearchTerm}</span>`;
    return this.htmlTranslatedPipe.transform('LearningSearch_NoResultsFound', {
      termHtml,
    });
  }

  private get showingResultsFor(): SafeHtml {
    // This HTML contains a user entered value so we sanitize it
    const termHtml = this.htmlPipe
      .transform`<span class="italic font-semibold">${this.searchTerm}</span>`;
    return this.htmlTranslatedPipe.transform(
      'LearningSearch_ShowingResultsFor',
      { termHtml }
    );
  }

  public get header(): string | SafeHtml {
    return this.typoSuggestionAutoSearched
      ? this.searchedInsteadFor
      : this.didYouMean;
  }

  public get subtext(): string | SafeHtml {
    return this.typoSuggestionAutoSearched
      ? this.noResultsFound
      : this.showingResultsFor;
  }

  public selectSuggestedTerm(event: Event): void {
    if ((<HTMLElement>event.target)?.tagName === 'A') {
      this.searchTrackerService.searchTypoSuggestionClicked(
        this.searchTerm,
        this.suggestedTerm
      );
      this.suggestionSelected.emit(this.suggestedTerm);
    }
  }

  public keydownHandler(event: KeyboardEvent) {
    if (isKey(event, Key.Enter, Key.Space)) {
      event.preventDefault();
      this.selectSuggestedTerm(event);
    }
  }
}
